import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import { ILocationItem } from '../../../../../components/custom/LocationTree';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import * as enums from '../../../../../enums';
import { infoScreenDisplayType, locationType } from '../../../../../enums';
import * as models from '../../../../../models';
import { mapLocationsToOptions } from '../../../../../utils/mappers';

interface IInfoScreenCreateModalProps {
    refetchInfoScreens(): Promise<void>;
    basedOnInitialValues: models.IInfoScreenListItem | null;
    openInfoScreenCreateModalWindow: (basedOnInitialValues: models.IInfoScreenListItem | null) => Promise<void>;
}

const validationSchema = Yup.object<models.IInfoScreen>().shape({
    name: Yup.string().required('Šis laukas yra privalomas'),
    model: Yup.string().required('Šis laukas yra privalomas'),
    displayType: Yup.number().required('Šis laukas yra privalomas'),
    screenSoundType: Yup.number().required('Šis laukas yra privalomas')
});

const initialState: models.IInfoScreenListItem = {
    id: 0,
    name: '',
    model: '',
    displayType: enums.infoScreenDisplayType.Locations,
    locationId: null,
    connectionStatus: false,
    locationName: '',
    screenSoundType: enums.screenSoundType.None,
    workScheduleRowsDisplayType: enums.workScheduleRowsDisplayType.Horizontal
};

const InfoScreenCreateModalWindow: FC<IWindowComponentProps<IInfoScreenCreateModalProps>> = (props) => {
    const {
        componentProps: { refetchInfoScreens, openInfoScreenCreateModalWindow, basedOnInitialValues },
        onClose
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [createNext, setCreateNext] = useState<boolean>(false);
    const [floorTypeLocations, setFloorTypeLocations] = useState<ILocationItem[] | null>(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const resp = await api.administration.getLocations(locationType.Level);
            setFloorTypeLocations(resp);
        } catch (ex) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: models.IInfoScreenListItem, formikActions: formik.FormikHelpers<models.IInfoScreenListItem>) => {
        try {
            setIsLoading(true);
            formikActions.setStatus();
            await api.administration.createInfoScreen(values);
            toast.success('Ekranas sėkmingai sukurtas');
            await refetchInfoScreens();
            onClose();
            if (createNext) {
                await openInfoScreenCreateModalWindow(values);
            }
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            formikActions.setSubmitting(false);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    <formik.Formik
                        validationSchema={validationSchema}
                        initialValues={basedOnInitialValues === null ? initialState : { ...basedOnInitialValues, name: '' }}
                        onSubmit={handleSubmit}
                        render={(props) => (
                            <formik.Form>
                                <div className="ms-Grid-row">
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="name"
                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    {...field}
                                                    label="Pavadinimas"
                                                    placeholder="Įrašykite info ekrano pavadinimą"
                                                    errorMessage={(props.status && props.status.name) || (form.errors.name && form.errors.name)}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="model"
                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    {...field}
                                                    label="Modelis"
                                                    placeholder="Įrašykite info ekrano modelį"
                                                    errorMessage={(props.status && props.status.model) || (form.errors.model && form.errors.model)}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="ms-Grid-row">
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="displayType"
                                            render={({ field, form }: formik.FieldProps<enums.InfoScreenDisplayType>) => (
                                                <fabric.Dropdown
                                                    label="Atvaizdavimo tipas"
                                                    selectedKey={field.value}
                                                    options={enums.infoScreenDisplayType.displayTypesArray()}
                                                    defaultSelectedKey={form.values.displayType}
                                                    onChange={(_, item) => form.setFieldValue(field.name, item && item.key)}
                                                    errorMessage={
                                                        (props.status && props.status.displayType) ||
                                                        (form.errors.displayType && form.errors.displayType)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="screenSoundType"
                                            render={({ field, form }: formik.FieldProps<enums.ScreenSoundType>) => (
                                                <fabric.Dropdown
                                                    defaultSelectedKey={field.value}
                                                    {...field}
                                                    onChange={(_, option?: fabric.IDropdownOption) =>
                                                        option && form.setFieldValue(field.name, option.key)
                                                    }
                                                    label="Skambėjimo tipas"
                                                    options={enums.screenSoundType.displayTypesArray()}
                                                    errorMessage={
                                                        (props.status && props.status.screenSoundType) ||
                                                        (form.errors.screenSoundType && form.errors.screenSoundType)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="ms-Grid-row">
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="locationId"
                                            render={({ field, form }: formik.FieldProps<number>) => (
                                                <fabric.Dropdown
                                                    options={mapLocationsToOptions(floorTypeLocations, true)}
                                                    defaultSelectedKey={field.value}
                                                    {...field}
                                                    onChange={(_, option?: fabric.IDropdownOption) =>
                                                        option && form.setFieldValue(field.name, option.key)
                                                    }
                                                    label="Aukštas"
                                                    errorMessage={
                                                        (props.status && props.status.locationId) ||
                                                        (form.errors.locationId && form.errors.locationId)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="url"
                                            render={() => (
                                                <>
                                                    <fabric.Label style={{ marginBottom: '5px' }}>Nuoroda į įrenginio rodomą vaizdą</fabric.Label>
                                                    <fabric.Link disabled>Nuoroda nepasiekiama kol įrenginys nėra sukurtas</fabric.Link>
                                                </>
                                            )}
                                        />
                                    </div>
                                </div>

                                {props.values.displayType === infoScreenDisplayType.Schedule && (
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="workScheduleRowsDisplayType"
                                                render={({ field, form }: formik.FieldProps<enums.ScreenSoundType>) => (
                                                    <fabric.Dropdown
                                                        defaultSelectedKey={field.value}
                                                        {...field}
                                                        onChange={(_, option?: fabric.IDropdownOption) =>
                                                            option && form.setFieldValue(field.name, option.key)
                                                        }
                                                        label="Tvarkaraščio ekrano tipas"
                                                        options={enums.workScheduleRowsDisplayType.displayTypesArray()}
                                                        errorMessage={
                                                            (props.status && props.status.workScheduleRowsDisplayType) ||
                                                            (form.errors.workScheduleRowsDisplayType && form.errors.workScheduleRowsDisplayType)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                    <fabric.Checkbox
                                        label="Kurti kitą"
                                        checked={createNext}
                                        onChange={() => setCreateNext(!createNext)}
                                    ></fabric.Checkbox>
                                    <fabric.PrimaryButton
                                        className="ms-slideRightIn40 ms-textAlignRight"
                                        text="Išsaugoti"
                                        disabled={props.isSubmitting || !props.isValid || !props.dirty}
                                        type="submit"
                                    />
                                </div>
                            </formik.Form>
                        )}
                    />
                </>
            )}
        </>
    );
};

export default InfoScreenCreateModalWindow;
